/**
 * Tab bar above the canvas listing every diagram in the active branch.
 * Click a tab to switch the active diagram; double-click to rename;
 * right-click for a small menu with rename + delete. The plus button
 * opens an inline form to create a new diagram (name + view kind).
 *
 * The tabs subscribe to the workspace revision so adding/renaming/
 * deleting a diagram anywhere refreshes the list.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api } from "../../api/client";
import { getRegistry, resolveBilingual } from "../../registry";
import { claimObjectLock, releaseObjectLock } from "../../state/objectLocks";
import { useSession } from "../../state/session";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import { AskClaudeDialog } from "../ai/AskClaudeDialog";
import { DIAGRAM_TEMPLATES } from "../templates/riskSecurityAssessment";
import { downloadDiagramPng, downloadDiagramSvg } from "./exportDiagram";

export function DiagramTabs() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  // Alleen kijken: geen nieuw diagram, niet hernoemen, geen Claude en geen
  // export (Gerald, 2026-10-17: "ook niet exporteren"). Tabs wisselen en
  // sluiten mag.
  const readOnly = useSession((s) => s.readOnly);
  const revision = useWorkspaceData((s) => s.revision);
  const activeDiagramId = useWorkspaceData((s) => s.activeDiagramId);
  const openDiagramIds = useWorkspaceData((s) => s.openDiagramIds);
  // Glossary (Sharp Attribuuttypes) is een pseudo-tab: geen diagram-rij maar
  // wel een tab in de bar. `glossaryTabOpen` = tab zichtbaar; `glossaryFocused`
  // = de Glossary is de actieve view (geen enkel diagram dan blauw).
  const glossaryTabOpen = useUi((s) => s.glossaryTabOpen);
  const glossaryFocused = useUi((s) => s.glossaryFocused);
  const focusGlossary = useUi((s) => s.focusGlossary);
  const blurGlossary = useUi((s) => s.blurGlossary);
  const closeGlossaryTab = useUi((s) => s.closeGlossaryTab);
  void revision;

  // Only diagrams whose tab is open show up in the tab bar. Order
  // matches the open-list order so the user can mentally rearrange via
  // close + re-open from the Diagrams panel if needed.
  const diagrams = useMemo(() => {
    const store = useWorkspaceData.getState().store;
    return openDiagramIds
      .map((id) => store.getDiagram(id))
      .filter((d): d is NonNullable<typeof d> => d !== undefined);
  }, [revision, openDiagramIds]);

  const [renamingId, setRenamingId] = useState<string | null>(null);
  /** Het diagram waarvan wíj het slot vasthouden. Een diagram hernoemen is
   *  hetzelfde soort bewerking als een object hernoemen: twee mensen die het
   *  tegelijk doen hebben geen verzoenbare uitkomst. */
  const renameLock = useRef<string | null>(null);

  /** Bewerkstand meteen aan, slot ernáást — zie `ConceptNode.tsx`. */
  const beginRename = (diagramId: string) => {
    if (useSession.getState().readOnly) return;
    setRenamingId(diagramId);
    void claimObjectLock(diagramId).then((denial) => {
      if (!denial) {
        renameLock.current = diagramId;
        return;
      }
      setRenamingId((current) => (current === diagramId ? null : current));
      useWorkspaceData.setState({
        lastError: denial.byBatch
          ? t("locks.heldByBatch")
          : t("locks.heldBy", { holder: denial.holder }),
      });
    });
  };

  const endRename = () => {
    const target = renameLock.current;
    renameLock.current = null;
    setRenamingId(null);
    if (target) releaseObjectLock(target);
  };
  const [creating, setCreating] = useState(false);
  // Right-click context-menu state op een tab. `diagramId` houdt
  // welke tab is geklikt; `x` / `y` zijn de viewport-coords waarop de
  // popup opent. null = geen menu open.
  const [tabMenu, setTabMenu] = useState<{ diagramId: string; x: number; y: number } | null>(null);
  // Sluit het menu via Escape of een click elders. We sluiten ook bij
  // window-blur zodat de popup nooit blijft hangen wanneer de
  // browser focus verliest.
  useEffect(() => {
    if (!tabMenu) return;
    const close = () => setTabMenu(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", onKey);
    window.addEventListener("blur", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("blur", close);
    };
  }, [tabMenu]);

  // F2-shortcut bridge: Editor.tsx dispatcht `ae:rename-diagram` met de
  // diagramId van het te hernoemen diagram. Als die diagram momenteel
  // in de tab-bar staat (open is) starten we de inline-rename; anders
  // is 't aan DiagramsPanel om 'm op te pikken (out of scope voor M6.5).
  useEffect(() => {
    const onRename = (e: Event) => {
      const detail = (e as CustomEvent<{ diagramId: string }>).detail;
      if (!detail?.diagramId) return;
      if (!openDiagramIds.includes(detail.diagramId)) return;
      beginRename(detail.diagramId);
    };
    window.addEventListener("ae:rename-diagram", onRename);
    return () => window.removeEventListener("ae:rename-diagram", onRename);
  }, [openDiagramIds]);

  const closeAllTabs = () => {
    if (!workspaceId) return;
    // Snapshot vooraf — closeDiagramTab muteert openDiagramIds, dus
    // 't oorspronkelijke array door-itereren zou items overslaan.
    const ids = [...openDiagramIds];
    const ws = useWorkspaceData.getState();
    for (const id of ids) ws.closeDiagramTab(workspaceId, id);
  };
  const closeAllExcept = (keepId: string) => {
    if (!workspaceId) return;
    const ids = openDiagramIds.filter((id) => id !== keepId);
    const ws = useWorkspaceData.getState();
    for (const id of ids) ws.closeDiagramTab(workspaceId, id);
  };

  return (
    <div className="ledger flex items-center gap-1 border-(--ld-border) border-b bg-(--ld-surface) px-2 py-1">
      <ul className="flex flex-1 flex-wrap items-center gap-1">
        {glossaryTabOpen ? (
          <li>
            <GlossaryTab
              name={t("glossary.title")}
              active={glossaryFocused}
              onClick={focusGlossary}
              onCloseRequested={closeGlossaryTab}
            />
          </li>
        ) : null}
        {diagrams.map((d) => {
          // Terwijl de Glossary gefocust is, mag geen enkel diagram blauw zijn.
          const isActive = d.id === activeDiagramId && !glossaryFocused;
          const displayName = resolveBilingual(d.name, lang) || t("diagramTabs.unnamed");
          if (renamingId === d.id) {
            return (
              <li key={d.id}>
                <RenameInput
                  initial={displayName}
                  onCommit={(text) => {
                    if (workspaceId && branchId && text.trim() && text !== displayName) {
                      // Inline rename only updates the active language;
                      // the DiagramsPanel offers full bilingual editing.
                      const langKey: "nl" | "en" = lang === "en" ? "en" : "nl";
                      void useWorkspaceData.getState().renameDiagram(workspaceId, branchId, d.id, {
                        ...d.name,
                        [langKey]: text.trim(),
                      });
                    }
                    endRename();
                  }}
                  onCancel={endRename}
                />
              </li>
            );
          }
          return (
            <li key={d.id}>
              <DiagramTab
                name={displayName}
                active={isActive}
                onClick={() => {
                  // Klik op een diagram-tab = dat diagram focussen én de
                  // Glossary-focus weghalen (de Glossary-tab blijft staan).
                  useWorkspaceData.getState().setActiveDiagram(d.id);
                  blurGlossary();
                }}
                onRenameRequested={() => beginRename(d.id)}
                onCloseRequested={() => {
                  if (!workspaceId) return;
                  // Tab × button = close the tab WITHOUT deleting the
                  // diagram. The Diagrammen-paneel can re-open it later
                  // (and offers a real delete with cascade-confirm).
                  useWorkspaceData.getState().closeDiagramTab(workspaceId, d.id);
                }}
                onContextMenuRequested={(x, y) => setTabMenu({ diagramId: d.id, x, y })}
              />
            </li>
          );
        })}
        {creating && workspaceId && branchId ? (
          <li>
            <CreateInline
              lang={lang}
              onCommit={async (name, choice) => {
                // Sluit de form METEEN, vóór de async translate-call
                // (zie bug-comment in pre-2026-05-27-versie).
                setCreating(false);
                const langKey: "nl" | "en" = lang === "en" ? "en" : "nl";
                const otherKey: "nl" | "en" = langKey === "nl" ? "en" : "nl";
                const nameObj: Record<string, string> = { [langKey]: name };
                try {
                  const res = await api.translate({
                    source_lang: langKey,
                    target_lang: otherKey,
                    text: name,
                    kind: "name",
                  });
                  if (res.text) nameObj[otherKey] = res.text;
                } catch (err) {
                  if (!(err instanceof ApiError && err.status === 412)) {
                    console.warn("auto-translate diagram name failed", err);
                  }
                }
                if (choice.kind === "viewKind") {
                  void useWorkspaceData
                    .getState()
                    .createDiagram(workspaceId, branchId, nameObj, choice.viewKindId);
                } else {
                  void useWorkspaceData
                    .getState()
                    .createDiagramFromTemplate(workspaceId, branchId, choice.template, nameObj);
                }
              }}
              onCancel={() => setCreating(false)}
            />
          </li>
        ) : readOnly ? null : (
          <li>
            <button
              type="button"
              className="rounded border border-dashed border-(--ld-control) px-2 py-0.5 text-(--ld-muted) text-xs hover:border-(--ld-muted) hover:text-(--ld-text)"
              onClick={() => setCreating(true)}
              title={t("diagramTabs.newTitle")}
            >
              + {t("diagramTabs.newLabel")}
            </button>
          </li>
        )}
      </ul>
      {/* Right-aligned actions: "Vraag Claude" + Export. Both disabled
          when there's no active diagram. */}
      {activeDiagramId && !glossaryFocused && !readOnly && (
        <div className="flex items-center gap-1">
          <AskClaudeButton />
          <ExportMenu activeDiagramId={activeDiagramId} lang={lang} />
        </div>
      )}
      {tabMenu && (
        <div
          className="fixed z-50 min-w-[240px] rounded border border-(--ld-control) bg-(--ld-surface) py-1 text-xs shadow-lg"
          style={{ left: tabMenu.x, top: tabMenu.y }}
          // Voorkom dat de "klik buiten 't menu = close"-listener
          // afgaat wanneer de modelleur op een menu-item klikt — anders
          // sluit het menu vóór de onClick van het item firet.
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="block w-full px-3 py-1.5 text-left text-(--ld-text) hover:bg-(--ld-raised)"
            onClick={() => {
              if (!workspaceId) return;
              useWorkspaceData.getState().closeDiagramTab(workspaceId, tabMenu.diagramId);
              setTabMenu(null);
            }}
          >
            {t("diagramTabs.menu.closeDiagram")}
          </button>
          <button
            type="button"
            className="block w-full px-3 py-1.5 text-left text-(--ld-text) hover:bg-(--ld-raised) disabled:cursor-not-allowed disabled:text-(--ld-muted)"
            disabled={openDiagramIds.length <= 1}
            onClick={() => {
              closeAllExcept(tabMenu.diagramId);
              setTabMenu(null);
            }}
          >
            {t("diagramTabs.menu.closeOthers")}
          </button>
          <button
            type="button"
            className="block w-full px-3 py-1.5 text-left text-(--ld-text) hover:bg-(--ld-raised)"
            onClick={() => {
              closeAllTabs();
              setTabMenu(null);
            }}
          >
            {t("diagramTabs.menu.closeAll")}
          </button>
        </div>
      )}
    </div>
  );
}

/** Small button that opens the "Vraag Claude" modal. */
function AskClaudeButton() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // F9-shortcut bridge: Editor.tsx dispatcht `ae:open-ask-claude`. We
  // ignoreren 't event als de modal al open is — Escape sluit 'm dan
  // alsnog netjes.
  useEffect(() => {
    const onOpen = () => setOpen((cur) => cur || true);
    window.addEventListener("ae:open-ask-claude", onOpen);
    return () => window.removeEventListener("ae:open-ask-claude", onOpen);
  }, []);
  return (
    <>
      <button
        type="button"
        className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 font-medium text-(--ld-text) text-xs hover:bg-(--ld-active)"
        onClick={() => setOpen(true)}
        title={t("diagramTabs.askClaudeTitle")}
        aria-keyshortcuts="F9"
      >
        {t("diagramTabs.askClaude")}
      </button>
      <AskClaudeDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/** Per-active-diagram export dropdown (SVG / PNG). */
function ExportMenu({ activeDiagramId, lang }: { activeDiagramId: string; lang: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Subscribe to revision so the diagram-name in the filename stays
  // fresh after a rename without needing a remount.
  const revision = useWorkspaceData((s) => s.revision);
  void revision;

  const exportArgs = () => {
    const store = useWorkspaceData.getState().store;
    const diagram = store.getDiagram(activeDiagramId);
    if (!diagram) return null;
    const filenameStem = resolveBilingual(diagram.name, lang) || "diagram";
    const langKey: "nl" | "en" = lang === "en" ? "en" : "nl";
    return {
      diagram,
      concepts: store.listConcepts(),
      relations: store.listRelations(),
      lang: langKey,
      filenameStem,
    };
  };

  const exportSvg = async () => {
    const args = exportArgs();
    if (!args) return;
    setBusy(true);
    try {
      await downloadDiagramSvg(args);
    } catch (err) {
      console.error("SVG export failed:", err);
      alert(err instanceof Error ? err.message : t("diagramTabs.export.svgFailed"));
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };
  const exportPng = async () => {
    const args = exportArgs();
    if (!args) return;
    setBusy(true);
    try {
      await downloadDiagramPng(args);
    } catch (err) {
      console.error("PNG export failed:", err);
      alert(err instanceof Error ? err.message : t("diagramTabs.export.pngFailed"));
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 text-(--ld-text) text-xs hover:bg-(--ld-raised)"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        title={t("diagramTabs.export.title")}
      >
        {busy ? "…" : "↓ Export"}
      </button>
      {open && !busy && (
        <ul
          className="absolute top-full right-0 z-20 mt-1 flex w-32 flex-col rounded border border-(--ld-control) bg-(--ld-surface) text-xs shadow-lg"
          // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: export-dropdown als menu; role="menu" is bewust op de <ul>
          role="menu"
        >
          <li>
            <button
              type="button"
              className="block w-full px-3 py-1.5 text-left text-(--ld-text) hover:bg-(--ld-raised)"
              onClick={() => void exportSvg()}
              role="menuitem"
            >
              SVG
            </button>
          </li>
          <li>
            <button
              type="button"
              className="block w-full px-3 py-1.5 text-left text-(--ld-text) hover:bg-(--ld-raised)"
              onClick={exportPng}
              role="menuitem"
            >
              PNG (2×)
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

function DiagramTab({
  name,
  active,
  onClick,
  onRenameRequested,
  onCloseRequested,
  onContextMenuRequested,
}: {
  name: string;
  active: boolean;
  onClick: () => void;
  onRenameRequested: () => void;
  onCloseRequested: () => void;
  onContextMenuRequested: (x: number, y: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={`group flex items-center gap-1 rounded px-2 py-0.5 text-xs ${
        active
          ? "border border-(--ld-accent) bg-(--ld-active) text-(--ld-accent)"
          : "border border-transparent bg-(--ld-active) text-(--ld-text) hover:bg-(--ld-raised)"
      }`}
      role="tab"
      onContextMenu={(e) => {
        e.preventDefault();
        onContextMenuRequested(e.clientX, e.clientY);
      }}
    >
      <button
        type="button"
        className="cursor-pointer"
        onClick={onClick}
        onDoubleClick={onRenameRequested}
        title={t("diagramTabs.tabTitle")}
      >
        {name}
      </button>
      <button
        type="button"
        className="text-(--ld-muted) opacity-0 hover:text-(--ld-negative) group-hover:opacity-100"
        onClick={onCloseRequested}
        title={t("diagramTabs.closeTabTitle")}
        aria-label={t("diagramTabs.closeTab")}
      >
        ×
      </button>
    </div>
  );
}

/**
 * Pseudo-tab voor de virtuele Glossary-view (Sharp Attribuuttypes). Geen
 * diagram-rij, dus geen rename / context-menu — alleen focussen (klik) en
 * sluiten (×). Visueel identiek aan een diagram-tab met een tabel-glyph.
 */
function GlossaryTab({
  name,
  active,
  onClick,
  onCloseRequested,
}: {
  name: string;
  active: boolean;
  onClick: () => void;
  onCloseRequested: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={`group flex items-center gap-1 rounded px-2 py-0.5 text-xs ${
        active
          ? "border border-(--ld-accent) bg-(--ld-active) text-(--ld-accent)"
          : "border border-transparent bg-(--ld-active) text-(--ld-text) hover:bg-(--ld-raised)"
      }`}
    >
      <button
        type="button"
        className="flex cursor-pointer items-center gap-1"
        onClick={onClick}
        title={name}
      >
        <span aria-hidden>≣</span>
        {name}
      </button>
      <button
        type="button"
        className="text-(--ld-muted) opacity-0 hover:text-(--ld-negative) group-hover:opacity-100"
        onClick={onCloseRequested}
        title={t("diagramTabs.closeTab")}
        aria-label={t("diagramTabs.closeTab")}
      >
        ×
      </button>
    </div>
  );
}

function RenameInput({
  initial,
  onCommit,
  onCancel,
}: {
  initial: string;
  onCommit: (name: string) => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(initial);
  return (
    <input
      ref={ref}
      className="rounded border border-(--ld-accent) bg-(--ld-surface) px-2 py-0.5 text-xs focus:outline-none"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") onCommit(value);
        else if (e.key === "Escape") onCancel();
      }}
      onBlur={() => onCommit(value)}
    />
  );
}

/**
 * Definieer 5 vaste hoofdgroeperingen voor het nieuw-diagram-form.
 * Elke groep heeft een bilingual label + een filter dat bepaalt welke
 * view-kinds erin vallen (op basis van view-kind-id-prefix). Templates
 * worden expliciet per groep gekoppeld via `templateIds`.
 *
 * Groepen die niet matchen op een view-kind verschijnen niet in de
 * dropdown — bv. wanneer een workspace alleen ArchiMate-types heeft
 * actief, zien gebruikers geen BMC-groep.
 */
const DIAGRAM_GROUPS: Array<{
  id: string;
  /** i18n key of the group label. */
  labelKey: string;
  /** Predicate that picks the view-kinds belonging to this group. */
  match: (viewKindId: string) => boolean;
  /** Template-ids die in deze groep verschijnen (naast view-kinds). */
  templateIds: string[];
}> = [
  {
    id: "archimate",
    labelKey: "diagramTabs.groups.archimate",
    match: (id) => id.startsWith("archimate."),
    templateIds: [],
  },
  {
    id: "risk-security",
    labelKey: "diagramTabs.groups.riskSecurity",
    match: (id) => id.startsWith("risk."),
    templateIds: ["risk.security-assessment-template"],
  },
  {
    id: "sharp",
    labelKey: "diagramTabs.groups.sharp",
    match: (id) => id.startsWith("sharp."),
    templateIds: [],
  },
  {
    id: "c4",
    labelKey: "diagramTabs.groups.c4",
    match: (id) => id.startsWith("c4."),
    templateIds: [],
  },
  {
    id: "bmc",
    labelKey: "diagramTabs.groups.bmc",
    match: (id) => id.startsWith("bmc."),
    templateIds: [],
  },
  {
    id: "analytics",
    labelKey: "diagramTabs.groups.analytics",
    match: (id) => id.startsWith("analytics."),
    templateIds: [],
  },
  {
    id: "roadmap",
    labelKey: "diagramTabs.groups.roadmap",
    match: (id) => id.startsWith("roadmap."),
    templateIds: [],
  },
];

function CreateInline({
  lang,
  onCommit,
  onCancel,
}: {
  lang: string;
  onCommit: (
    name: string,
    choice:
      | { kind: "viewKind"; viewKindId: string }
      | { kind: "template"; template: (typeof DIAGRAM_TEMPLATES)[number] },
  ) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const submittedRef = useRef(false);
  // Alle view-kinds in de registry, plus templates uit DIAGRAM_TEMPLATES.
  // Gefilterd per gekozen groep.
  const revision = useWorkspaceData((s) => s.revision);
  const allViewKinds = useMemo(() => {
    void revision;
    return (
      getRegistry()
        .listActiveViewKinds()
        .slice()
        // Glossary is geen user-creatable diagram: 't is een virtuele view die via
        // de Explorer-node "Attribuuttypes" opent. Niet in de Nieuw-picker.
        .filter((vk) => vk.layout_mode !== "glossary")
        .sort((a, b) =>
          resolveBilingual(a.name, lang).localeCompare(resolveBilingual(b.name, lang)),
        )
    );
  }, [lang, revision]);

  // Singleton-view-kinds (Gap/Plateau, layout_mode plateau-diff/gap-diff) zijn
  // één-per-WP: verberg ze uit de nieuw-diagram-picker zodra er al een diagram
  // van dat type bestaat (op main; via de branch-overlay zichtbaar in de store).
  const existingViewKinds = useMemo(() => {
    void revision;
    return new Set(
      useWorkspaceData
        .getState()
        .store.listDiagrams()
        .map((d) => d.viewKind),
    );
  }, [revision]);

  // Welke groepen hebben überhaupt iets te kiezen? Verberg lege groepen.
  const activeGroups = useMemo(() => {
    return DIAGRAM_GROUPS.filter((g) => {
      const hasVks = allViewKinds.some((vk) => g.match(vk.id));
      const hasTpls = g.templateIds.some((tid) => DIAGRAM_TEMPLATES.some((t) => t.id === tid));
      return hasVks || hasTpls;
    });
  }, [allViewKinds]);

  // Default-groep = eerste actieve groep (meestal ArchiMate).
  const [groupId, setGroupId] = useState<string>(() => activeGroups[0]?.id ?? "archimate");
  const currentGroup = activeGroups.find((g) => g.id === groupId) ?? activeGroups[0];

  // Choices binnen de huidige groep — eerst view-kinds, dan templates,
  // beide alfabetisch.
  type Choice =
    | { kind: "viewKind"; id: string; label: string }
    | { kind: "template"; id: string; label: string };
  const choices: Choice[] = useMemo(() => {
    if (!currentGroup) return [];
    const vkChoices: Choice[] = allViewKinds
      .filter((vk) => currentGroup.match(vk.id))
      .filter((vk) => {
        const isSingleton = vk.layout_mode === "plateau-diff" || vk.layout_mode === "gap-diff";
        return !(isSingleton && existingViewKinds.has(vk.id));
      })
      .map((vk) => ({
        kind: "viewKind" as const,
        id: vk.id,
        label: resolveBilingual(vk.name, lang) + (vk.custom ? " ★" : ""),
      }));
    const tplChoices: Choice[] = DIAGRAM_TEMPLATES.filter((t) =>
      currentGroup.templateIds.includes(t.id),
    ).map((t) => ({
      kind: "template" as const,
      id: t.id,
      label: `${resolveBilingual(t.label, lang)} 📋`,
    }));
    return [...vkChoices, ...tplChoices].sort((a, b) => a.label.localeCompare(b.label, lang));
  }, [currentGroup, allViewKinds, lang, existingViewKinds]);

  // Default choice = eerste in de groep. Reset bij group-switch.
  const [choiceKey, setChoiceKey] = useState<string>(() => {
    const first = choices[0];
    return first ? `${first.kind}:${first.id}` : "";
  });
  // Als groep wijzigt of choices niet langer matchen, reset choiceKey.
  const validChoiceKeys = choices.map((c) => `${c.kind}:${c.id}`);
  useEffect(() => {
    if (!validChoiceKeys.includes(choiceKey)) {
      const first = choices[0];
      setChoiceKey(first ? `${first.kind}:${first.id}` : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, choices.length]);

  const commitOnce = (text: string) => {
    if (submittedRef.current) return;
    const sep = choiceKey.indexOf(":");
    if (sep < 0) return;
    const kind = choiceKey.slice(0, sep);
    const id = choiceKey.slice(sep + 1);
    submittedRef.current = true;
    if (kind === "template") {
      const tpl = DIAGRAM_TEMPLATES.find((t) => t.id === id);
      if (!tpl) {
        submittedRef.current = false;
        return;
      }
      onCommit(text, { kind: "template", template: tpl });
    } else {
      onCommit(text, { kind: "viewKind", viewKindId: id });
    }
  };

  return (
    <div className="flex items-center gap-1 rounded border border-(--ld-accent) bg-(--ld-active) px-2 py-0.5">
      <input
        className="w-32 rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 text-xs focus:outline-none"
        placeholder={t("diagramTabs.create.namePlaceholder")}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && name.trim() && choiceKey) commitOnce(name.trim());
          else if (e.key === "Escape") onCancel();
        }}
      />
      <select
        className="rounded border border-(--ld-control) bg-(--ld-surface) px-1 py-0.5 text-xs focus:outline-none"
        value={groupId}
        onChange={(e) => setGroupId(e.target.value)}
        title={t("diagramTabs.create.groupTitle")}
      >
        {activeGroups.map((g) => (
          <option key={g.id} value={g.id}>
            {t(g.labelKey)}
          </option>
        ))}
      </select>
      <select
        className="rounded border border-(--ld-control) bg-(--ld-surface) px-1 py-0.5 text-xs focus:outline-none"
        value={choiceKey}
        onChange={(e) => setChoiceKey(e.target.value)}
        title={t("diagramTabs.create.typeTitle")}
      >
        {choices.map((c) => (
          <option key={`${c.kind}:${c.id}`} value={`${c.kind}:${c.id}`}>
            {c.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="rounded bg-(--ld-primary) px-2 py-0.5 text-(--ld-primary-ink) text-xs hover:bg-(--ld-primary-hover) disabled:bg-(--ld-active)"
        disabled={!name.trim() || !choiceKey}
        onClick={() => commitOnce(name.trim())}
      >
        OK
      </button>
      <button
        type="button"
        className="rounded px-1 text-(--ld-muted) text-xs hover:text-(--ld-text)"
        onClick={onCancel}
      >
        ×
      </button>
    </div>
  );
}
